import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { AdminPageHeader } from "./_helpers";
import { Trash2, RotateCcw, Clock, Newspaper } from "lucide-react";
import { toast } from "sonner";

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [restoring, setRestoring] = useState({});

  const load = () => api.get("/audit-logs?limit=500").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  /** Last-24h deleted news, newest first. Includes soft + hard deletes. */
  const recentDeletions = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return items
      .filter((a) => a.entity === "news"
        && (a.action === "delete_soft" || a.action === "delete_hard" || a.action === "delete"))
      .filter((a) => new Date(a.timestamp).getTime() >= cutoff)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [items]);

  const softDeleteCount = recentDeletions.filter((a) => a.action === "delete_soft").length;
  const hardDeleteCount = recentDeletions.filter((a) => a.action === "delete_hard").length;

  const restoreOne = async (entityId) => {
    if (!entityId) return;
    setRestoring((r) => ({ ...r, [entityId]: true }));
    try {
      await api.post(`/news/${entityId}/restore`);
      toast.success("Haber geri yüklendi");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri yüklenemedi (kalıcı silinmiş olabilir)");
    } finally {
      setRestoring((r) => ({ ...r, [entityId]: false }));
    }
  };

  return (
    <div>
      <AdminPageHeader title="Audit Log" subtitle="İşlem geçmişi" />

      {/* Last-24h deleted news widget */}
      <section
        className="bg-white border border-gray-100 rounded-lg p-4 mb-4"
        data-testid="audit-recent-deletions-widget"
      >
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h3 className="font-bold flex items-center gap-2">
                Son 24 Saatte Silinen Haberler
                <span
                  data-testid="audit-recent-deletions-count"
                  className="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700"
                >
                  {recentDeletions.length}
                </span>
              </h3>
              <div className="text-[11px] text-gray-500 flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <span>{softDeleteCount} çöp kutusuna atıldı • {hardDeleteCount} kalıcı silindi</span>
              </div>
            </div>
          </div>
        </div>
        {recentDeletions.length === 0 ? (
          <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded p-4 text-center">
            Son 24 saatte silinmiş haber yok. Sistem güvende ✅
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase text-left">
                <tr>
                  <th className="py-1.5">Tarih</th>
                  <th className="py-1.5">Silen</th>
                  <th className="py-1.5">Tür</th>
                  <th className="py-1.5">Haber ID</th>
                  <th className="py-1.5 text-right">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {recentDeletions.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="py-1.5 text-xs text-gray-600 whitespace-nowrap">
                      {new Date(a.timestamp).toLocaleString("tr-TR")}
                    </td>
                    <td className="py-1.5 text-xs">{a.user_email || "—"}</td>
                    <td className="py-1.5">
                      {a.action === "delete_hard" ? (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          Kalıcı
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          Çöp Kutusu
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-[11px] font-mono text-gray-500 truncate max-w-[180px]">
                      {a.entity_id || "—"}
                    </td>
                    <td className="py-1.5 text-right">
                      {a.action === "delete_soft" ? (
                        <button
                          type="button"
                          disabled={!!restoring[a.entity_id]}
                          onClick={() => restoreOne(a.entity_id)}
                          data-testid={`audit-restore-${a.entity_id}`}
                          className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <RotateCcw className="w-3 h-3" />
                          {restoring[a.entity_id] ? "..." : "Geri Yükle"}
                        </button>
                      ) : (
                        <span className="text-[11px] text-gray-400 inline-flex items-center gap-1">
                          <Newspaper className="w-3 h-3" /> geri alınamaz
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Full audit log */}
      <div className="bg-white border border-gray-100 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">Tarih</th>
              <th className="px-3 py-2">Kullanıcı</th>
              <th className="px-3 py-2">Aksiyon</th>
              <th className="px-3 py-2">Varlık</th>
              <th className="px-3 py-2">Detay</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="px-3 py-2 text-xs">{new Date(a.timestamp).toLocaleString("tr-TR")}</td>
                <td className="px-3 py-2 text-xs">{a.user_email}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    a.action === "delete_hard" ? "bg-red-100 text-red-700" :
                    a.action === "delete_soft" ? "bg-amber-100 text-amber-800" :
                    a.action === "restore" || a.action === "restore_all" ? "bg-emerald-100 text-emerald-700" :
                    "bg-gray-100"
                  }`}>
                    {a.action}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{a.entity}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{a.details}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-gray-500">Kayıt yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
